import { Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { QuestionModel } from '../api/models/question-model';
import { AnswerModel } from '../api/models/answer-model';
import { QuestionnaireInstanceService } from '../api/services/questionnaire-instance.service';
import { TranslationEventService } from './translation-event.service';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class QuestionsService {
  currentQuestion = signal<QuestionModel | null>(null);

  constructor(
    private questionnaireInstanceService: QuestionnaireInstanceService,
    private translationEventService: TranslationEventService,
  ) {}

  /*
    Initialises the question service, setting the current question to the one provided in the parameter
   */
  setCurrentQuestion(question: QuestionModel) {
    this.currentQuestion.set(question);
  }

  /*
    Set the current question to the next logical question in the sequence, based on the answer to the current question
   */
  goToNextQuestion(
    questionnaireInstanceGuid: string,
    answer: AnswerModel,
  ): Observable<QuestionModel> {
    return this.questionnaireInstanceService.nextPost$Json({
      questionnaireInstanceGuid: questionnaireInstanceGuid,
      body: answer,
      language: this.translationEventService.selectedLanguage().code,
    });
  }

  /*
    Set the current question to the previous logical question in the sequence
  */
  goToPreviousQuestion(
    questionnaireInstanceGuid: string,
  ): Observable<QuestionModel> {
    return this.questionnaireInstanceService.previousPost$Json({
      questionnaireInstanceGuid: questionnaireInstanceGuid,
      language: this.translationEventService.selectedLanguage().code,
    });
  }

  /**
   * Refresh the current question with the latest data from the server, this is currently only setup to refresh a couple of fields
   * Which are the htmlText, language, text, descriptionText and answerOptions
   * @param questionnaireInstanceGuid
   */
  refreshCurrentQuestion(
    questionnaireInstanceGuid: string,
  ): Observable<QuestionModel> {
    return this.questionnaireInstanceService
      .resumePost$Json({
        questionnaireInstanceGuid: questionnaireInstanceGuid,
        language: this.translationEventService.selectedLanguage().code,
      })
      .pipe(
        tap((question) => {
          this.currentQuestion.update((currentQuestion) => {
            if (!currentQuestion) {
              return question;
            }

            return {
              ...currentQuestion,
              htmlText: question.htmlText,
              language: question.language,
              text: question.text,
              descriptionText: question.descriptionText,
              questionSetIntroductionText: question.questionSetIntroductionText,
              answerOptions: question.answerOptions,
            };
          });
        }),
      );
  }

  resumeInstance(questionnaireInstanceGuid: string): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.questionnaireInstanceService
        .resumePost$Json({
          questionnaireInstanceGuid: questionnaireInstanceGuid,
        })
        .subscribe({
          next: (question) => {
            this.questionnaireInstanceService.questionnaireInstanceGuid =
              questionnaireInstanceGuid;
            this.setCurrentQuestion(question);
            observer.next(true);
            observer.complete();
          },
          error: (err) => {
            console.error('Failed to resume questionnaire instance:', err);
            observer.next(false);
            observer.complete();
          },
        });
    });
  }
}
